import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Globe, School, X, Mic } from 'lucide-react';
import { doc, getDoc, collection, query, where, getDocs, addDoc, serverTimestamp, onSnapshot, deleteDoc, updateDoc } from 'firebase/firestore';
import { db, auth, analytics } from '../firebase';
import { logEvent } from 'firebase/analytics';
import sparkLogo from '../assets/spark-logo.png';
import { useIdentity } from '../context/IdentityContext';

const statusMessages = [
  'Scanning frequencies...',
  'Finding a voice match...',
  'Almost there...',
];

const randomNames = [
  'Mystic', 'Shadow', 'Echo', 'Blaze', 'Luna', 'Nova', 'Sky', 'Zephyr', 'Raven',
  'Vortex', 'Nebula', 'Drift', 'Vibe', 'Frost', 'Phantom', 'Wraith', 'Dusk',
  'Haze', 'Storm', 'Rogue', 'Pulse', 'Ember', 'Flicker', 'Shade', 'Bolt', 'Glimmer',
  'Surge', 'Flare', 'Cosmo', 'Daze', 'Sable', 'Nyx', 'Rift', 'Glow', 'Specter',
  'Chill', 'Havoc', 'Mist', 'Stryke', 'Zest', 'Dawn', 'Volt', 'Crimson', 'Slate',
  'Kaze', 'Fury', 'Vapor', 'Glint', 'Rebel', 'Ash', 'Tide', 'Zenith', 'Dagger',
  'Star', 'Nerve', 'Blitz', 'Shadez', 'Flux', 'Neon', 'Chaos', 'Verve', 'Rush',
  'Grit', 'Faze', 'Drip', 'Hex', 'Jolt', 'Wisp', 'Scorch', 'Vex', 'Hype', 'Frosty',
  'Slash', 'Glaze', 'Sparkz', 'Eclipse', 'Raze', 'Sway', 'Phantomz', 'Buzz', 'Quartz',
  'Vivid', 'RUSH', 'Craze', 'Driftz', 'Ignite', 'Zest', 'Frenzy', 'Spectral', 'BlazeX',
  'Vaporz', 'Sting', 'Aurora', 'Kryo'
];

const getRandomAlias = () => randomNames[Math.floor(Math.random() * randomNames.length)];

export const VoiceSetupPage = () => {
  const { identityMode } = useIdentity();
  const navigate = useNavigate();
  const location = useLocation();
  const initialMode = location.state?.selectedMode || 'global';
  const userCollege = location.state?.userCollege || 'Unknown';

  const [currentMode, setCurrentMode] = useState(initialMode);
  const [statusIndex, setStatusIndex] = useState(0);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [isInitializing, setIsInitializing] = useState(true);
  const [micGranted, setMicGranted] = useState(false);
  const [avatarIndex, setAvatarIndex] = useState(0);
  const [canSearch, setCanSearch] = useState(false);
  const [showCampusTimeout, setShowCampusTimeout] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  
  const hasNavigatedRef = useRef(false);
  const queueDocIdRef = useRef(null);
  const minimumTimeRef = useRef(Date.now() + 2500);

  // Route Protection: Prevent direct URL access
  useEffect(() => {
    if (!location.state || !location.state.selectedMode) {
      navigate('/mode-select', { replace: true, state: { featureType: 'voice1v1' } });
    }
  }, [location, navigate]);

  // Cycling Status Texts
  useEffect(() => {
    const statusInterval = setInterval(() => {
        setStatusIndex((prev) => (prev + 1) % statusMessages.length);
    }, 3000);
    return () => clearInterval(statusInterval);
  }, []);

  // Timer
  useEffect(() => {
    const timerInterval = setInterval(() => {
      setTimerSeconds((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(timerInterval);
  }, []);

  // Cycling Avatars
  useEffect(() => {
    const avatarInterval = setInterval(() => {
        setAvatarIndex((prev) => (prev + 2) % randomNames.length);
    }, 1500);
    return () => clearInterval(avatarInterval);
  }, []);

  // Request Microphone permissions immediately
  useEffect(() => {
      const getPermissions = async () => {
          try {
              const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
              // Stop tracks immediately, we just wanted permission
              stream.getTracks().forEach(track => track.stop());
              setMicGranted(true);
              setIsInitializing(false);
          } catch (e) {
            //   console.error("Mic denied:", e);
              toast.error("Microphone permission is required for Voice Calls.");
              navigate('/mode-select', { state: { featureType: 'voice1v1' } });
          }
      };
      getPermissions();
  }, [navigate]);

  useEffect(() => {
    if (currentMode === 'campus' && timerSeconds >= 5) {
        setShowCampusTimeout(true);
    } else {
        setShowCampusTimeout(false);
    }
  }, [currentMode, timerSeconds]);

  const switchToGlobal = async () => {
      // Delete old campus queue doc — the useEffect will re-run and create a new global one
      if (queueDocIdRef.current) {
          const oldId = queueDocIdRef.current;
          queueDocIdRef.current = null;
          await deleteDoc(doc(db, 'voiceMatchQueue', oldId)).catch(() => {});
      }
      
      logEvent(analytics, 'switch_to_global_clicked', { type: '1-1_voice' });
      setCurrentMode('global');
      setShowCampusTimeout(false);
  };

  // Search Delay Timer
  useEffect(() => {
      const delayTimer = setTimeout(() => {
          setCanSearch(true);
      }, 1500);
      return () => clearTimeout(delayTimer);
  }, []);

  // Queue Logic
  useEffect(() => {
    if (!micGranted || isInitializing || !canSearch) return;

    const user = auth.currentUser;
    if (!user) {
      navigate('/login');
      return;
    }

    let isMatched = false;

    // 1. Listen for voice room creation
    const q1 = query(collection(db, 'voiceRooms'), where('user1', '==', user.uid), where('ended', '==', false));
    const q2 = query(collection(db, 'voiceRooms'), where('user2', '==', user.uid), where('ended', '==', false));

    const handleNewRoom = (snapshot) => {
        snapshot.docChanges().forEach((change) => {
            if (change.type === 'added' && !hasNavigatedRef.current) {
                hasNavigatedRef.current = true;
                const delay = Math.max(0, minimumTimeRef.current - Date.now());
                setTimeout(() => {
                    const roomId = change.doc.id;
                    
                    // Remove from queue
                    if (queueDocIdRef.current) {
                        deleteDoc(doc(db, 'voiceMatchQueue', queueDocIdRef.current)).catch(()=>{});
                    }
                    
                    const roomData = change.doc.data();
                    navigate(`/voice-chat/${roomId}`, { replace: true, state: { roomData } });
                }, delay);
            }
        });
    };

    const unsub1 = onSnapshot(q1, handleNewRoom);
    const unsub2 = onSnapshot(q2, handleNewRoom);

    // 2. Add to Queue or Match Client-Side
    const attemptMatchOrQueue = async () => {
      if (queueDocIdRef.current || hasNavigatedRef.current || isMatched) return;
      
      try {
        const queueRef = collection(db, 'voiceMatchQueue');
        const qSearch = query(queueRef, where('mode', '==', currentMode));
        const snap = await getDocs(qSearch);
        
        let partner = null;
        for (const d of snap.docs) {
            const data = d.data();
            if (data.uid !== user.uid) {
                if (currentMode === 'campus') {
                    if (data.college === userCollege) {
                        partner = { docId: d.id, ...data };
                        break;
                    }
                } else {
                    partner = { docId: d.id, ...data };
                    break;
                }
            }
        }

        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const uData = userDoc.data() || {};

        if (partner) {
            // console.log("Partner found! Attempting to create Voice Room...");
            isMatched = true;
            hasNavigatedRef.current = true;
            
            // MATCH FOUND! Create room
            const roomData = {
                user1: partner.uid,
                user1Alias: partner.alias || getRandomAlias(),
                user1College: partner.college || 'Unknown',
                user2: user.uid,
                user2Alias: getRandomAlias(),
                user2College: uData.college || 'Unknown',
                createdAt: serverTimestamp(),
                ended: false,
                connectionState: 'new',
                mode: currentMode
            };
            
            try {
                const roomRef = await addDoc(collection(db, 'voiceRooms'), roomData);
                // console.log("Voice Room successfully generated via Firestore Rules:", roomRef.id);
                
                // Delete partner from queue so no one else hits them
                await deleteDoc(doc(db, 'voiceMatchQueue', partner.docId)).catch(e => console.error("Could not delete Partner from Queue:", e));
                
                // Go to room directly
                navigate(`/voice-chat/${roomRef.id}`, { 
                    replace: true, 
                    state: { roomData: { user1: partner.uid, user2: user.uid } } 
                });
            } catch(roomErr) {
                // console.error("Firestore Rules Rejected VoiceRoom Creation:", roomErr);
            }
            
        } else {
            // console.log("No Match Found. Attempting to enter queue...");
            // NO MATCH. Add self to queue
            const existingQ = query(queueRef, where('uid', '==', user.uid));
            const existingSnap = await getDocs(existingQ);
            
            // Wait for existing to delete before adding a new one
            for (const d of existingSnap.docs) {
                await deleteDoc(d.ref).catch(e => console.error("Could not delete old queue doc:", e));
            }
            
            const queueDataPayload = {
                uid: user.uid,
                mode: currentMode,
                college: uData.college || 'Unknown',
                alias: getRandomAlias(),
                timestamp: serverTimestamp()
            };
            // console.log("Queue Payload:", queueDataPayload);
            
            try {
                const docRef = await addDoc(queueRef, queueDataPayload);
                // console.log("Successfully queued up!", docRef.id);
                queueDocIdRef.current = docRef.id;
                
                logEvent(analytics, 'join_queue', { 
                    type: '1-1_voice', 
                    initial_mode: currentMode 
                });
            } catch(qErr) {
                // console.error("Firestore Rules Rejected Queue Insertion:", qErr);
            }
        }
      } catch (e) {
        // console.error("Queue add error", e);
      }
    };

    attemptMatchOrQueue();

    // 3. Queue Listener to catch stuck players
    const qSearchActive = query(collection(db, 'voiceMatchQueue'), where('mode', '==', currentMode));
    const unsubQueue = onSnapshot(qSearchActive, async (snap) => {
        if (isMatched || hasNavigatedRef.current || !queueDocIdRef.current) return;
        
        let partner = null;
        for (const d of snap.docs) {
            const data = d.data();
            if (data.uid !== user.uid) {
                if (currentMode === 'campus') {
                    if (data.college === userCollege) partner = { docId: d.id, ...data };
                } else {
                    partner = { docId: d.id, ...data };
                }
            }
            if (partner) break;
        }

        // Only one of them should create the room to prevent race conditions
        if (partner && user.uid < partner.uid) {
            isMatched = true;
            hasNavigatedRef.current = true;
            
            const userDoc = await getDoc(doc(db, 'users', user.uid));
            const uData = userDoc.data() || {};
            
            const roomData = {
                user1: partner.uid,
                user1Alias: partner.alias || getRandomAlias(),
                user1College: partner.college || 'Unknown',
                user2: user.uid,
                user2Alias: getRandomAlias(),
                user2College: uData.college || 'Unknown',
                createdAt: serverTimestamp(),
                ended: false,
                connectionState: 'new',
                mode: currentMode
            };
            
            try {
                const roomRef = await addDoc(collection(db, 'voiceRooms'), roomData);
                await deleteDoc(doc(db, 'voiceMatchQueue', partner.docId)).catch(()=>{});
                
                const delay = Math.max(0, minimumTimeRef.current - Date.now());
                setTimeout(() => {
                    navigate(`/voice-chat/${roomRef.id}`, { 
                        replace: true, 
                        state: { roomData: { user1: partner.uid, user2: user.uid } } 
                    });
                }, delay);
            } catch(e) {}
        }
    });

    // Cleanups
    return () => {
        unsub1();
        unsub2();
        unsubQueue();
        if (queueDocIdRef.current && !hasNavigatedRef.current) {
            deleteDoc(doc(db, 'voiceMatchQueue', queueDocIdRef.current)).catch(() => {});
        }
    };
  }, [micGranted, isInitializing, navigate, currentMode, userCollege, canSearch]);

  const handleCancelSearch = async () => {
    const user = auth.currentUser;
    if (user && queueDocIdRef.current) {
        try {
            hasNavigatedRef.current = true;
            await deleteDoc(doc(db, 'voiceMatchQueue', queueDocIdRef.current));
            navigate('/mode-select', { state: { featureType: 'voice1v1' } });
        } catch(e) {
            // console.error("Error deleting queue document:", e);
            navigate('/mode-select', { state: { featureType: 'voice1v1' } }); // Still navigate even if delete fails
        }
    } else {
        navigate('/mode-select', { state: { featureType: 'voice1v1' } });
    }
  };

  const formatTime = (seconds) => {
      const m = Math.floor(seconds / 60);
      const s = seconds % 60;
      return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  if (identityMode === 'real') {
     return (
       <main className="w-full sm:w-[700px] min-h-screen border-r border-gray-800 flex flex-col items-center justify-center p-8 shrink-0 relative bg-[#0a0a0a]">
          <div className="w-16 h-16 rounded-full bg-[#0BD9E5]/20 text-[#0BD9E5] flex items-center justify-center mb-6">
             <Phone size={32} />
          </div>
          <h2 className="text-[31px] font-extrabold mb-2 leading-tight text-center text-white">Voice Call</h2>
          <p className="text-gray-500 text-[15px] text-center">Coming soon for Real Identity mode.</p>
       </main>
     );
  }

  if (isInitializing) {
     return (
        <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6 text-center">
            <Mic className="text-[#0BD9E5] w-12 h-12 mb-6 animate-pulse" />
            <h2 className="text-white text-xl font-bold mb-2">Microphone Access</h2>
            <p className="text-gray-400 text-sm">Please allow microphone access to initiate the voice connection.</p>
        </div>
     );
  }

  return (
    <main className="w-full sm:w-[700px] min-h-screen border-r border-gray-800 flex flex-col shrink-0 relative overflow-hidden bg-black text-white font-poppins selection:bg-[#0BD9E5]/30">
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
            <div className="absolute top-[20%] left-[20%] w-[40%] h-[40%] bg-[#0BD9E5]/10 blur-[100px] rounded-full mix-blend-screen animate-pulse" style={{ animationDuration: '4s' }} />
            <div className="absolute bottom-[20%] right-[20%] w-[40%] h-[40%] bg-purple-500/10 blur-[100px] rounded-full mix-blend-screen animate-pulse" style={{ animationDuration: '5s' }} />
        </div>

        <div className="relative z-10 flex flex-col h-full items-center justify-center p-6 w-full max-w-md mx-auto">
            <div className="absolute top-4 left-4 right-4 flex justify-between items-center z-50">
                <button 
                    onClick={handleCancelSearch}
                    className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors pointer-events-auto"
                >
                    <X size={20} className="text-white" />
                </button>
                <div className="w-10" />
            </div>

           {/* Avatar & Radar Centered Together */}
           <div className="relative w-72 h-72 flex items-center justify-center mb-8">
               {/* Radars */}
               <div className={`absolute inset-0 border-2 rounded-full bg-black/40 flex items-center justify-center ${currentMode === 'campus' ? 'border-[#0BD9E5]' : 'border-[#BD00FF]'}`}>
                  {[0, 1, 2].map((i) => (
                     <motion.div 
                         key={i}
                         initial={{ scale: 0.5, opacity: 0.8 }}
                         animate={{ scale: 1.5, opacity: 0 }}
                         transition={{ duration: 2, repeat: Infinity, delay: i * 0.6 }}
                         className={`absolute w-40 h-40 border rounded-full ${currentMode === 'campus' ? 'border-[#0BD9E5]' : 'border-[#BD00FF]'}`}
                     />
                  ))}
               </div>

               {/* Avatar */}
               <motion.div 
                   animate={{ 
                      boxShadow: currentMode === 'campus' ? ['0 0 0px #0BD9E5', '0 0 40px #0BD9E5', '0 0 0px #0BD9E5'] : ['0 0 0px #BD00FF', '0 0 40px #BD00FF', '0 0 0px #BD00FF']
                   }}
                   transition={{ duration: 2, repeat: Infinity }}
                   className={`w-24 h-24 bg-[#111] border rounded-full flex items-center justify-center relative z-10 overflow-hidden ${currentMode === 'campus' ? 'border-[#0BD9E5]/50' : 'border-[#BD00FF]/50'}`}
               >
                    <AnimatePresence mode="wait">
                        <motion.img
                            key={avatarIndex}
                            src={`/avatars/${randomNames[avatarIndex]}.png`}
                            alt="avatar"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            transition={{ duration: 0.3 }}
                            className="w-full h-full object-cover"
                        />
                    </AnimatePresence>
               </motion.div>
           </div>

           <AnimatePresence mode="wait">
               <motion.p 
                   key={statusIndex}
                   initial={{ opacity: 0, y: 10 }}
                   animate={{ opacity: 1, y: 0 }}
                   exit={{ opacity: 0, y: -10 }}
                   className="text-gray-300 text-lg font-medium text-center px-6 min-h-[28px] tracking-wide"
               >
                   {statusMessages[statusIndex]}
               </motion.p>
           </AnimatePresence>
           
           <p className="font-mono text-[#0BD9E5] font-bold text-lg mt-6 bg-[#0BD9E5]/10 px-4 py-1.5 rounded-full tracking-widest border border-[#0BD9E5]/20">
               {formatTime(timerSeconds)}
           </p>

           <div className="mt-12 flex flex-col items-center gap-4 w-full">
               {currentMode === 'global' ? (
                   <div className="px-5 py-2 rounded-full border border-[#BD00FF]/50 bg-[#BD00FF]/10 text-[#BD00FF] text-xs font-bold uppercase tracking-widest flex items-center gap-2 shadow-[0_0_15px_rgba(189,0,255,0.2)]">
                      <Globe size={14} /> Global Resonance
                   </div>
               ) : (
                   <div className="px-5 py-2 rounded-full border border-[#0BD9E5]/50 bg-[#0BD9E5]/10 text-[#0BD9E5] text-xs font-bold uppercase tracking-widest flex items-center gap-2 shadow-[0_0_15px_rgba(11,217,229,0.2)]">
                      <School size={14} /> Campus Specific
                   </div>
               )}

               <AnimatePresence>
                   {showCampusTimeout && (
                       <motion.button
                           initial={{ opacity: 0, y: 10 }}
                           animate={{ opacity: 1, y: 0 }}
                           exit={{ opacity: 0, y: -10 }}
                           onClick={switchToGlobal}
                           disabled={isSwitching}
                           className="px-6 py-3 rounded-2xl bg-white/10 border border-white/20 text-white text-sm font-bold flex items-center gap-2 hover:bg-white/20 transition-all disabled:opacity-50"
                       >
                           {isSwitching ? 'Switching...' : (
                               <>
                                  <Globe size={16} className="text-[#BD00FF]" /> Switch to Global
                               </>
                           )}
                       </motion.button>
                   )}
               </AnimatePresence>
           </div>
        </div>
    </main>
  );
};

export default VoiceSetupPage;
